import React from "react";
import { ArrowUpFromDot, ScanEye, SlidersHorizontal, Sparkles } from "lucide-react";
import { InspectorVisibilityEyeButton } from "../components/ui/InspectorVisibilityEyeButton";
import { InspectorVisibilitySection } from "../components/property-panels/InspectorVisibilitySection";
import VisibilityIconRow from "../components/property-panels/VisibilityIconRow";
import { useJointVisibilityControls } from "./useJointVisibilityControls";
import { useViewerActions, useViewerStore } from "../stores/viewerStore";
import { useSelectionHighlightControl } from "../selected-highlight-visibility";

const JointVisibilityToolbar = ({
  jointUuid,
  bodyUuid,
  className = "",
  sectionTitle = "Joint Visibility",
}) => {
  const jointVizVisibility = useViewerStore((s) => s.jointVizVisibility);
  const viewerActions = useViewerActions();
  const thisJointVisible = jointUuid ? (jointVizVisibility[jointUuid] ?? true) : true;
  const { state, disabled, focusDisabled, toggleFocus, toggleFrameAxes, togglePositionSlider } =
    useJointVisibilityControls({ jointUuid, bodyUuid, enabled: true });
  const { highlight, toggleHighlight } = useSelectionHighlightControl({
    targetType: "joint",
    targetId: jointUuid,
    disabled,
    ensureVisible: () => {
      if (!useViewerStore.getState().showJoints) viewerActions.toggleShowJoints();
    },
  });
  const buttons = [
    {
      key: "highlight",
      title: "Highlight selected joint",
      on: highlight,
      onClick: toggleHighlight,
      disabled,
      Icon: Sparkles,
    },
    {
      key: "focus",
      title: "Display selected joint only",
      on: state.focus,
      onClick: toggleFocus,
      disabled: focusDisabled,
      Icon: ScanEye,
    },
    {
      key: "frame-axes",
      title: "Show frame axes",
      on: state.frameAxes,
      onClick: toggleFrameAxes,
      disabled,
      Icon: ArrowUpFromDot,
    },
    {
      key: "position-slider",
      title: "Show joint position slider in scene",
      on: state.positionSlider,
      onClick: togglePositionSlider,
      disabled,
      Icon: SlidersHorizontal,
    },
  ];

  return (
    <InspectorVisibilitySection
      sectionTitle={sectionTitle}
      className={className}
      headerActions={
        <div className="flex items-center gap-1">
          <InspectorVisibilityEyeButton
            visible={thisJointVisible}
            onToggle={() =>
              jointUuid && viewerActions.setJointVizVisible(jointUuid, !thisJointVisible)
            }
            tooltipWhenOn="This joint marker is visible — click to hide"
            tooltipWhenOff="This joint marker is hidden — click to show"
            ariaLabel="Toggle this joint marker visibility"
          />
        </div>
      }
    >
      <VisibilityIconRow actions={buttons} />
    </InspectorVisibilitySection>
  );
};

export default React.memo(JointVisibilityToolbar);
